/**
 * Input Sanitization and Validation Security Module
 * Protects against XSS, injection attacks, and malicious input
 */

/**
 * HTML encode special characters to prevent XSS
 */
export function htmlEncode(str) {
  if (typeof str !== 'string') return str;

  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
}

/**
 * Check if string appears to be base64 encoded data
 */
function isBase64Data(str) {
  // Check for common base64 patterns and length
  if (typeof str !== 'string' || str.length < 100) return false;
  
  // Base64 alphabet check and typical image headers
  const base64Pattern = /^[A-Za-z0-9+/=]+$/;
  const imageHeaders = ['/9j/', 'iVBORw0KGgo', 'R0lGOD', 'UklGR']; // JPEG, PNG, GIF, WebP
  
  // Check if it's a clean base64 string with image signature
  if (base64Pattern.test(str) && imageHeaders.some(header => str.startsWith(header))) {
    return true;
  }
  
  return false;
}

/**
 * Sanitize string input by removing potentially dangerous characters
 */
export function sanitizeString(input, options = {}) {
  if (typeof input !== 'string') return input;

  const {
    maxLength = 1000,
    allowHtml = false,
    allowNewlines = true,
    stripScripts = true
  } = options;

  // Don't sanitize base64 image data to prevent corruption
  if (isBase64Data(input)) {
    return input;
  }

  let sanitized = input;

  // Remove script tags and content
  if (stripScripts) {
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  }

  // HTML encode if HTML not allowed
  if (!allowHtml) {
    sanitized = htmlEncode(sanitized);
  }

  // Remove newlines if not allowed
  if (!allowNewlines) {
    sanitized = sanitized.replace(/[\r\n\t]/g, ' ');
  }

  // Truncate to max length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  // Remove null bytes and control characters
  sanitized = sanitized.replace(/[\x00-\x08\x0E-\x1F\x7F]/g, '');

  return sanitized.trim();
}

/**
 * Sanitize query parameters for API requests
 */
export function sanitizeQueryParams(params) {
  if (!params || typeof params !== 'object') return {};

  const sanitized = {};

  for (const [key, value] of Object.entries(params)) {
    // Sanitize key
    const cleanKey = sanitizeString(key, { maxLength: 100, allowHtml: false });

    if (!cleanKey) continue;

    // Sanitize value based on type
    if (typeof value === 'string') {
      sanitized[cleanKey] = sanitizeString(value, { maxLength: 500 });
    } else if (typeof value === 'number') {
      // Validate number is safe
      if (Number.isFinite(value) && Math.abs(value) < Number.MAX_SAFE_INTEGER) {
        sanitized[cleanKey] = value;
      }
    } else if (typeof value === 'boolean') {
      sanitized[cleanKey] = value;
    } else if (Array.isArray(value)) {
      // Sanitize array elements
      sanitized[cleanKey] = value
        .slice(0, 20) // Limit array size
        .map(item => typeof item === 'string' ? sanitizeString(item) : item)
        .filter(item => item !== null && item !== undefined);
    }
  }

  return sanitized;
}

/**
 * Validate and sanitize request body
 */
export function sanitizeRequestBody(body) {
  if (!body || typeof body !== 'object') return body;

  if (Array.isArray(body)) {
    return body
      .slice(0, 100) // Limit array size
      .map(item => sanitizeRequestBody(item));
  }

  const sanitized = {};

  for (const [key, value] of Object.entries(body)) {
    const cleanKey = sanitizeString(key, { maxLength: 100, allowHtml: false });

    if (!cleanKey) continue;

    if (typeof value === 'string') {
      sanitized[cleanKey] = sanitizeString(value, {
        maxLength: 2000,
        allowNewlines: true
      });
    } else if (typeof value === 'number') {
      if (Number.isFinite(value) && Math.abs(value) < Number.MAX_SAFE_INTEGER) {
        sanitized[cleanKey] = value;
      }
    } else if (typeof value === 'boolean') {
      sanitized[cleanKey] = value;
    } else if (value && typeof value === 'object') {
      sanitized[cleanKey] = sanitizeRequestBody(value);
    }
  }

  return sanitized;
}

/**
 * Validate URL to prevent SSRF attacks
 */
export function validateUrl(url) {
  if (typeof url !== 'string') {
    return false;
  }

  // Allow relative URLs (starting with /)
  if (url.startsWith('/')) {
    // Basic validation for relative URLs
    // Prevent path traversal attempts
    if (url.includes('..') || url.includes('//')) {
      return false;
    }
    return true;
  }

  try {
    const parsed = new URL(url);

    // Only allow HTTPS in production for absolute URLs
    if (process.env.NODE_ENV === 'production' && parsed.protocol !== 'https:') {
      return false;
    }

    // Allow HTTP in development for absolute URLs
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return false;
    }

    // Prevent access to private IP ranges for absolute URLs
    const hostname = parsed.hostname.toLowerCase();
    const privatePatterns = [
      /^127\./, // 127.0.0.0/8
      /^10\./, // 10.0.0.0/8
      /^172\.(1[6-9]|2[0-9]|3[01])\./, // 172.16.0.0/12
      /^192\.168\./, // 192.168.0.0/16
      /^169\.254\./, // 169.254.0.0/16 (link-local)
      /^::1$/, // IPv6 loopback
      /^fe80:/, // IPv6 link-local
      /^fc00:/, // IPv6 unique local
      /localhost/i
    ];

    const isPrivate = privatePatterns.some(pattern => pattern.test(hostname));

    // In development mode, allow localhost and 127.0.0.1 for local development
    if (process.env.NODE_ENV !== 'production') {
      const isLocalhost = hostname === 'localhost' ||
                          hostname.endsWith('.localhost') ||
                          hostname.startsWith('127.') ||
                          hostname === '::1';

      if (isLocalhost) {
        return true;
      }
      // Allow other non-private URLs in development
      return !isPrivate;
    }

    // In production, block all private IPs
    return !isPrivate;
  } catch (error) {
    return false;
  }
}

/**
 * Rate limiting state for client-side protection
 */
const rateLimitState = new Map();

/**
 * Client-side rate limiting to prevent abuse
 */
export function checkRateLimit(endpoint, maxRequests = 60, windowMs = 60000) {
  const now = Date.now();
  const key = endpoint;

  if (!rateLimitState.has(key)) {
    rateLimitState.set(key, { requests: [], windowStart: now });
  }

  const state = rateLimitState.get(key);

  // Clean old requests outside the window
  state.requests = state.requests.filter(time => now - time < windowMs);

  // Check if limit exceeded
  if (state.requests.length >= maxRequests) {
    throw new Error('Rate limit exceeded. Please slow down your requests.');
  }

  // Add current request
  state.requests.push(now);

  return true;
}

/**
 * Clean up rate limit state periodically
 */
setInterval(() => {
  const now = Date.now();
  for (const [key, state] of rateLimitState.entries()) {
    if (now - state.windowStart > 300000) { // 5 minutes
      rateLimitState.delete(key);
    }
  }
}, 60000); // Clean every minute